import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionTrigger,
  Alert,
  Button,
  Checkbox,
  HelperText,
  Label,
  Modal,
  ModalActions,
  ModalContent,
  ModalTitleInverse,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TextInput,
} from '@c2fo/liquidity';
import { PercentIcon } from '@c2fo/liquidity/icons';
import { useCurrencyContext } from '@/contexts/CurrencyContext';
import { BuyerGroupWithStats } from '@/enterprise/data/useGroupByBuyer';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { hasMismatchedRateTypes } from '@/enterprise/features/globalOffers/utils';
import { Schedule } from '@/enterprise/features/offerModal/inputs/SchedulingInput';
import { validateCadence } from '@/enterprise/features/offerModal/utils';
import { useOfferDiscountAPRDisplay } from '@/enterprise/features/takerMarketTable/utils/useOfferDiscountAPRDisplay';
import { useReporting } from '@/reporting';
import useExchangeRates from '@/shared/data/useExchangeRates';
import convertCurrency from '@/utils/convertCurrency';
import getTakerMarketName from '@/utils/getTakerMarketName';
import localeRateToDomain from '@/utils/localeRateToDomain';
import { useLocaleFormat } from '@/utils/useLocaleFormat';
import { getNormalizedRate, isValidRateInput } from '@/utils/validateRate';
import FrequencyInput from './FrequencyInput';
import { OfferBuilderFrequency, RateType, useOfferBuilderContext } from './OfferBuilderContext';
import { defaultSchedule } from './OfferReview/EditScheduleModal';
import useMixedPricingInfo from './useMixedPricingInfo';
import {
  getNameYourRateMarkets,
  getMixedEligibleAmountInfo,
  getMarketsWithPositiveEligibleAmount,
  getMarketsWithZeroEligibleAmount,
  useRateTypeOptions,
  useValidateRate,
  ValidateRateResult,
  buildUpdateWorkingTakerMarketsRatePayload,
  createUniformRateMap,
} from './utils';

export type SetRateModalAccordionValue = 'Rate' | 'Frequency' | '';

export type SetRateModalProps =
  | {
      open: boolean;
      mode: undefined;
      buyer: undefined;
      takerMarket: undefined;
      onClose: () => void;
    }
  | {
      open: boolean;
      mode: 'initial';
      onClose: () => void;
      onNext: () => void;
    }
  | {
      open: boolean;
      mode: 'summary';
      onClose: () => void;
      initialRateType?: RateType;
      onNext?: () => void;
    }
  | {
      open: boolean;
      mode: 'buyer';
      buyer: BuyerGroupWithStats;
      onClose: () => void;
    }
  | {
      open: boolean;
      mode: 'takerMarket';
      takerMarket: TakerMarket;
      onClose: () => void;
    };

const SetRateModal = (props: SetRateModalProps) => {
  const {
    dispatch,
    state: { rateType, selected, frequency, schedule },
  } = useOfferBuilderContext();
  const { t } = useTranslation();
  const { trackEnterpriseEvent } = useReporting();
  const [internalRate, setInternalRate] = useState('');
  const [internalRateType, setInternalRateType] = useState<RateType | undefined>(rateType ?? undefined);
  const [internalFrequency, setInternalFrequency] = useState<OfferBuilderFrequency>(frequency ?? 'recurring');
  const [internalSchedule, setInternalSchedule] = useState<Schedule>(schedule ?? defaultSchedule);
  const rateInputRef = useRef<HTMLInputElement>(null);
  const rateOptions = useRateTypeOptions();
  const { asCurrency } = useLocaleFormat();
  const { validateRate } = useValidateRate();
  const { selectedCurrency } = useCurrencyContext();
  const { data: exchangeRates } = useExchangeRates();
  const [rateError, setRateError] = useState<ValidateRateResult | null>(null);
  const [rateTypeError, setRateTypeError] = useState<string | null>(null);
  const [cadenceError, setCadenceError] = useState<string | null>(null);
  const { offerDiscountAPRDisplay } = useOfferDiscountAPRDisplay();
  const [applyRateToAllCustomers, setApplyRateToAllCustomers] = useState(false);
  const [accordionValue, setAccordionValue] = useState<SetRateModalAccordionValue>('Rate');
  const { mode, onClose, open } = props;
  const buyer = 'buyer' in props ? props.buyer : undefined;
  const takerMarket = 'takerMarket' in props ? props.takerMarket : undefined;
  const onNext = 'onNext' in props ? props.onNext : undefined;
  const initialRateType = 'initialRateType' in props ? props.initialRateType : undefined;
  // convert internal rate to acceptable domain rate format. i.e. 3,5 -> 3.5
  const domainRate = localeRateToDomain(internalRate) ?? '';

  // focus on rate input
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => {
        if (rateInputRef.current) {
          rateInputRef.current.focus();
        }
      });

      setInternalRateType(initialRateType ?? rateType ?? undefined);
      setInternalFrequency(frequency ?? 'recurring');
      setInternalSchedule(schedule ?? defaultSchedule);

      trackEnterpriseEvent('offer-builder::rate-modal::opened', {
        mode: mode ?? 'initial',
        makerOrgUuid: buyer?.makerOrganizationUuid || takerMarket?.makerOrganizationUuid,
        marketUuid: takerMarket?.marketUuid,
        takerUuid: takerMarket?.takerDivisionUuid,
      });
    }
  }, [initialRateType, open, rateType, frequency, schedule, mode, buyer, takerMarket, trackEnterpriseEvent]);

  const getModeConfig = (mode: string | undefined, buyer?: BuyerGroupWithStats, takerMarket?: TakerMarket) => {
    if (mode === 'buyer' && buyer) {
      return {
        title: t('globalActions.modifyRate'),
        subtitle: t('builder.acrossAllBuyerDivisions', { buyerName: buyer.makerOrganizationName }),
        showDescription: false,
        buttonText: t('core.save'),
        allowRateTypeChange: false,
        showApplyToAll: true,
      };
    }

    if (mode === 'takerMarket' && takerMarket) {
      return {
        title: t('globalActions.modifyRate'),
        subtitle: getTakerMarketName(takerMarket!),
        showDescription: false,
        buttonText: t('core.save'),
        allowRateTypeChange: false,
        showApplyToAll: false,
      };
    }

    if (mode === 'summary') {
      return {
        title: t('globalActions.modifyRate'),
        subtitle: t('builder.setStartingRateModalSubtitle'),
        showDescription: false,
        buttonText: t('core.save'),
        allowRateTypeChange: false,
        showApplyToAll: false,
      };
    }

    return {
      title: t('core.setOfferLabel'),
      subtitle: null,
      showDescription: true,
      buttonText: t('core.next'),
      allowRateTypeChange: true,
      showApplyToAll: false,
    };
  };

  const getEligibleAmount = () => {
    if (mode === 'buyer' && buyer) {
      return buyer.stats.eligibleInvoiceAmount;
    }

    if (mode === 'takerMarket' && takerMarket) {
      return takerMarket.eligibleInvoiceAmount;
    }

    return selected.reduce(
      (acc, curr) =>
        acc +
        convertCurrency({
          amount: curr.eligibleInvoiceAmount,
          exchangeRates,
          from: curr.currency,
          to: selectedCurrency,
        }),
      0
    );
  };

  const modeConfig = getModeConfig(mode, buyer, takerMarket);
  const eligibleAmount = getEligibleAmount();

  // get the taker markets to check for mismatched rate types
  const takerMarketsToCheck = useMemo(() => {
    if (mode === 'buyer' && buyer) {
      return getNameYourRateMarkets(buyer.takerMarkets);
    }

    if (mode === 'takerMarket' && takerMarket) {
      return getNameYourRateMarkets([takerMarket]);
    }

    return getNameYourRateMarkets(selected);
  }, [mode, buyer, takerMarket, selected]);

  const selectedRateTypeFormatted = useMemo(() => {
    if (!internalRateType) return undefined;

    return internalRateType === 'APR' ? 'apr' : 'disc';
  }, [internalRateType]);

  // check if there are mismatched rate types
  const mismatchedRateTypes = useMemo(() => {
    return hasMismatchedRateTypes(takerMarketsToCheck, selectedRateTypeFormatted);
  }, [takerMarketsToCheck, selectedRateTypeFormatted]);

  // determine which markets to check based on mode
  const marketsToCheck = useMemo(() => {
    return mode === 'buyer' && buyer
      ? buyer.takerMarkets
      : mode === 'takerMarket' && takerMarket
      ? [takerMarket]
      : selected;
  }, [mode, buyer, takerMarket, selected]);

  // check for mixed pricing types
  const { displayAdditionalNameYourRateInfo, nameYourRateMarketsCount } = useMixedPricingInfo(marketsToCheck);

  // check for mixed eligible invoice amounts
  const mixedEligibleAmounts = useMemo(() => {
    return getMixedEligibleAmountInfo(marketsToCheck);
  }, [marketsToCheck]);

  const accordionTriggerClick = (e: React.MouseEvent<HTMLButtonElement>, value: SetRateModalAccordionValue) => {
    e.preventDefault();
    e.stopPropagation();
    setAccordionValue(value === accordionValue ? '' : value);
  };

  const handleClose = () => {
    trackEnterpriseEvent('offer-builder::rate-modal::closed', {
      mode: mode ?? 'initial',
      makerOrgUuid: buyer?.makerOrganizationUuid || takerMarket?.makerOrganizationUuid,
      marketUuid: takerMarket?.marketUuid,
      takerUuid: takerMarket?.takerDivisionUuid,
    });

    onClose();
    setInternalRate('');
    setInternalRateType(undefined);
    setInternalFrequency(frequency ?? 'recurring');
    setInternalSchedule(schedule ?? defaultSchedule);
    setApplyRateToAllCustomers(false);
    setRateError(null);
    setRateTypeError(null);
    setCadenceError(null);
  };

  const hasExistingSchedule = !!(
    internalSchedule.startDate ||
    internalSchedule.endDate ||
    (internalSchedule.cadence.type === 'weekly' && internalSchedule.cadence.value.length > 0)
  );

  const handleSave = () => {
    setRateError(null);
    setRateTypeError(null);
    setCadenceError(null);

    const missingRateType = !internalRateType;
    const missingRate = !internalRate || internalRate.trim() === '';

    if (missingRateType) {
      setRateTypeError(t('offer.selectRateType'));
    }

    if (missingRate) {
      setRateError({
        valid: false,
        errorMessage: t('offerSubmitDialog.errors.enterAValidRate'),
      });
    }

    if (missingRateType || missingRate) {
      if (mode === 'initial') {
        setAccordionValue('Rate');
        setTimeout(() => {
          if (rateInputRef.current) {
            rateInputRef.current.focus();
          }
        }, 300);
      }

      return;
    }

    const validationError = validateRate(domainRate, internalRateType);

    if (!validationError.valid) {
      setRateError(validationError);
      if (mode === 'initial') {
        setAccordionValue('Rate');
        setTimeout(() => {
          if (rateInputRef.current) {
            rateInputRef.current.focus();
          }
        }, 300);
      }

      return;
    }

    if (mode === 'initial' && internalFrequency === 'recurring' && hasExistingSchedule) {
      const cadenceValidation = validateCadence(internalSchedule.cadence, t);
      if (!cadenceValidation.valid) {
        setCadenceError(cadenceValidation.errorMessage || t('scheduling.selectDayOrReset'));
        setAccordionValue('Frequency');

        return;
      }
    }

    const rate = getNormalizedRate(domainRate);

    const getTakerMarketsToUpdate = () => {
      if (mode === 'buyer' && buyer) {
        const sourceTakerMarkets = applyRateToAllCustomers ? selected : buyer.takerMarkets;

        return getNameYourRateMarkets(sourceTakerMarkets);
      } else if (mode === 'takerMarket' && takerMarket) {
        return [takerMarket];
      } else {
        return getNameYourRateMarkets(selected);
      }
    };

    const takerMarketsToUpdate = getTakerMarketsToUpdate();

    dispatch({ type: 'SET_RATE', payload: rate });

    dispatch({
      type: 'UPDATE_WORKING_TAKER_MARKETS_RATE',
      payload: buildUpdateWorkingTakerMarketsRatePayload(
        takerMarketsToUpdate,
        createUniformRateMap(takerMarketsToUpdate, { rate, rateType: internalRateType })
      ),
    });

    const getScope = () => {
      if (mode === 'buyer') {
        return applyRateToAllCustomers ? 'all-customers' : 'buyer';
      }

      if (mode === 'takerMarket') {
        return 'division';
      }

      if (mode === 'summary') {
        return 'summary';
      }

      return 'initial';
    };

    trackEnterpriseEvent('offer-builder::rate::set', {
      rateValue: rate,
      rateType: internalRateType,
      previousRateType: rateType,
      scope: getScope(),
      frequency: internalFrequency,
      makerOrgUuid: buyer?.makerOrganizationUuid || takerMarket?.makerOrganizationUuid,
      marketUuid: takerMarket?.marketUuid,
      takerUuid: takerMarket?.takerDivisionUuid,
    });

    // for initial mode, update rate type, frequency, and schedule
    if (mode === 'initial') {
      dispatch({ type: 'SET_RATE_TYPE', payload: internalRateType });

      // update frequency - filter out $0 eligible divisions if one-time frequency is selected
      const filteredTakerMarkets =
        internalFrequency === 'oneTime' && mixedEligibleAmounts.hasMixedEligibleAmounts
          ? getMarketsWithPositiveEligibleAmount(selected)
          : selected;

      // remove $0 eligible markets from the selection
      if (filteredTakerMarkets.length !== selected.length) {
        const marketsToRemove = getMarketsWithZeroEligibleAmount(selected);
        dispatch({ type: 'TOGGLE_TAKER_MARKETS', payload: marketsToRemove });
      }

      dispatch({
        type: 'SET_FREQUENCY',
        payload: internalFrequency,
      });

      // set schedule (reset to default if one-time)
      dispatch({
        type: 'SET_SCHEDULE',
        payload: internalFrequency === 'oneTime' ? defaultSchedule : internalSchedule,
      });

      onNext?.();
    }

    // for summary mode, just call onNext (rate type handling is done by the caller)
    if (mode === 'summary') {
      onNext?.();
    }

    handleClose();
  };

  const renderRateInputs = () => (
    <div className="flex flex-col gap-6 sm:flex-row">
      <div className="w-full space-y-1 sm:w-1/2">
        <Label htmlFor="rate">{t('core.rate')}</Label>
        <TextInput
          ref={rateInputRef}
          id="rate"
          value={internalRate}
          onChange={(e) => {
            if (isValidRateInput(e.target.value)) {
              setInternalRate(e.target.value);
              setRateError(null);
            }
          }}
          placeholder={t('taker.setARate')}
          className="w-full"
          endIcon={PercentIcon}
          hasError={rateError && !rateError.valid}
        />
        {rateError && !rateError.valid && <HelperText variant="error">{rateError.errorMessage}</HelperText>}
      </div>
      <div className="w-full space-y-1 sm:w-1/2">
        <Label htmlFor="rateType">{t('core.rateType')}</Label>
        <Select
          disabled={!modeConfig.allowRateTypeChange}
          name="rateType"
          onValueChange={(value: RateType) => {
            setInternalRateType(value);
            setRateError(null);
            setRateTypeError(null);
          }}
          // Always pass a defined value ('' for "no selection") so the Select stays controlled
          // across renders — Radix warns if `value` toggles between undefined and a string.
          value={(modeConfig.allowRateTypeChange || mode === 'summary' ? internalRateType : rateType) ?? ''}
        >
          <SelectTrigger aria-label={t('core.rateType')} hasError={!!rateTypeError}>
            <SelectValue placeholder={t('offer.selectRateType')} />
          </SelectTrigger>
          <SelectContent>
            {rateOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                <p>{option.label}</p>
                <p className="block text-wrap text-sm [button_&]:hidden">{option.desc}</p>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {rateTypeError && <HelperText variant="error">{rateTypeError}</HelperText>}
      </div>
    </div>
  );

  const renderMismatchedRateTypesAlert = () => {
    if (selectedRateTypeFormatted && mismatchedRateTypes) {
      return (
        <div className="mt-4">
          <Alert
            type="warning"
            variant="outlined"
            description={
              selectedRateTypeFormatted === 'disc'
                ? t('globalOfferSubmitDialog.rateTypeMismatchWarningDiscount')
                : t('globalOfferSubmitDialog.rateTypeMismatchWarningApr')
            }
            full
          />
        </div>
      );
    }

    return null;
  };

  const renderApplyToAll = () => {
    if (modeConfig.showApplyToAll) {
      return (
        <div>
          <div className="my-6 border-[0.5px] border-stroke" />
          <div>
            <p className="font-bold">{t('builder.quickSet')}</p>
            <p>{t('builder.saveTimeApplyRate')}</p>
            <div className="flex items-center pt-2">
              <Checkbox
                id="applyToAllCustomers"
                className="bg-white"
                checked={applyRateToAllCustomers}
                onChange={() => setApplyRateToAllCustomers(!applyRateToAllCustomers)}
                size="sm"
              />
              <Label className="cursor-pointer pl-2.5 text-sm font-normal" htmlFor="applyToAllCustomers">
                {t('builder.applyRateAllCustomers')}
              </Label>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <Modal open={open} onClose={handleClose} className="sm:w-[750px] sm:min-w-0">
      <ModalTitleInverse>
        <span className="flex flex-col">
          <span>{modeConfig.title}</span>
          {modeConfig.subtitle && (
            <span className="text-sm font-normal text-text-secondary">{modeConfig.subtitle}</span>
          )}
        </span>
      </ModalTitleInverse>
      <ModalContent>
        {modeConfig.showDescription && <div className="mb-4">{t('builder.setOfferDescription')}</div>}
        {mode === 'initial' ? (
          <>
            {displayAdditionalNameYourRateInfo && (
              <div className="mb-4">
                <Alert
                  description={t('builder.rateAndFrequencyApplyOnlyToSelectedNameYourRateDivisions', {
                    divisionCount: nameYourRateMarketsCount.count,
                    totalCount: nameYourRateMarketsCount.total,
                  })}
                  full
                  type="info"
                  variant="outlined"
                />
              </div>
            )}
            <Accordion
              type="single"
              collapsible
              className="overflow-hidden rounded-md"
              defaultValue="Rate"
              value={accordionValue}
            >
              <AccordionItem divided value="IncludedAmount">
                <AccordionHeader className="flex-auto justify-between bg-canvas pr-14">
                  <div>{t('core.includedAmount')}</div>
                  <div className="font-semibold">{asCurrency(eligibleAmount, selectedCurrency)}</div>
                </AccordionHeader>
              </AccordionItem>
              <AccordionItem divided value="Rate">
                <AccordionHeader className="bg-canvas">
                  <div className="flex flex-auto justify-between">
                    <div>{t('core.rate')}</div>
                    <div className="font-semibold">
                      {internalRateType && domainRate && validateRate(domainRate, internalRateType).valid
                        ? offerDiscountAPRDisplay(parseFloat(domainRate), internalRateType)
                        : t('taker.setARate')}
                    </div>
                  </div>
                  <AccordionTrigger name="Rate Accordion Toggle" onClick={(e) => accordionTriggerClick(e, 'Rate')} />
                </AccordionHeader>
                <AccordionContent className="pb-0">
                  <div className="bg-blueGray-50 p-4">
                    {renderRateInputs()}
                    {renderMismatchedRateTypesAlert()}
                    {renderApplyToAll()}
                  </div>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem divided value="Frequency">
                <AccordionHeader className="bg-canvas">
                  <div className="flex flex-auto justify-between">
                    <div>{t('builder.frequency')}</div>
                    <div className="font-semibold">{t(`builder.${internalFrequency}`)}</div>
                  </div>
                  <AccordionTrigger
                    name="Frequency Accordion Toggle"
                    onClick={(e) => accordionTriggerClick(e, 'Frequency')}
                  />
                </AccordionHeader>
                <AccordionContent className="pb-0">
                  <div className="bg-blueGray-50">
                    <FrequencyInput
                      frequency={internalFrequency}
                      schedule={internalSchedule}
                      hasExistingSchedule={hasExistingSchedule}
                      onFrequencyChange={setInternalFrequency}
                      onScheduleChange={setInternalSchedule}
                      errors={cadenceError ? { cadence: { message: cadenceError } } : {}}
                      includeDescription={false}
                      takerMarkets={selected}
                    />
                    {mixedEligibleAmounts.hasMixedEligibleAmounts && internalFrequency === 'oneTime' && (
                      <div className="px-4 pb-4">
                        <Alert
                          description={t('builder.divisionsWithZeroEligibleAmounts', {
                            zeroEligibleCount: mixedEligibleAmounts.zeroEligibleCount,
                            totalCount: mixedEligibleAmounts.totalCount,
                          })}
                          full
                          type="warning"
                          variant="outlined"
                        />
                      </div>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </>
        ) : (
          <div className="overflow-hidden rounded-md bg-canvas">
            <div className="flex items-center justify-between border-b-[0.5px] border-stroke p-4">
              <div>{t('core.includedAmount')}</div>
              <div className="text-right font-medium">{asCurrency(eligibleAmount, selectedCurrency)}</div>
            </div>
            <div className="flex items-center justify-between border-b-[0.5px] border-stroke p-4 ">
              <div>{t('core.rate')}</div>
              <div className="text-right font-medium">
                {internalRateType && domainRate && validateRate(domainRate, internalRateType).valid
                  ? offerDiscountAPRDisplay(parseFloat(domainRate), internalRateType)
                  : t('taker.setARate')}
              </div>
            </div>
            <div className="bg-blueGray-50 px-4 py-6">
              {renderRateInputs()}
              {renderMismatchedRateTypesAlert()}
              {renderApplyToAll()}
            </div>
          </div>
        )}
      </ModalContent>
      <ModalActions>
        <Button variant="secondary" onClick={handleClose}>
          {t('core.cancel')}
        </Button>
        <Button variant="primary" onClick={handleSave}>
          {modeConfig.buttonText}
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default SetRateModal;
